// Gráficos leves em SVG, sem biblioteca (mesmo componente do app). Sempre com legenda (2+ séries), tooltip e números em texto.
import React, { useEffect, useRef, useState } from 'react';

export const CORES_SERIE = ['#1F5FAD', '#5BB0E6'];

function useLargura() {
  const ref = useRef(null); const [w, setW] = useState(0);
  useEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver(([e]) => setW(Math.floor(e.contentRect.width)));
    ro.observe(ref.current); return () => ro.disconnect();
  }, []);
  return [ref, w];
}

function escalaBonita(max) {
  if (max <= 0) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(max))); const f = max / p;
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * p;
}

export function Legenda({ series }) {
  if (series.length < 2) return null;
  return (
    <div className="graf-legenda">
      {series.map((s, i) => <span key={s.chave}><i style={{ background: s.cor || CORES_SERIE[i] }} />{s.nome}</span>)}
    </div>
  );
}

// dados: [{ rotulo, [chave]: valor }]; series: [{ chave, nome, cor? }]
export function Barras({ dados, series, formato = (v) => v, formatoEixo, altura = 220, vazio = 'Sem dados no período.' }) {
  const [ref, w] = useLargura();
  const [hover, setHover] = useState(null);
  const total = dados.reduce((s, d) => s + series.reduce((t, se) => t + Number(d[se.chave] || 0), 0), 0);
  const max = escalaBonita(Math.max(0, ...dados.flatMap((d) => series.map((s) => Number(d[s.chave] || 0)))));
  const esq = 64, dir = 8, top = 10, base = 26;
  const larg = Math.max(0, w - esq - dir), alt = altura - top - base;
  const grupo = dados.length ? larg / dados.length : 0;
  const gap = 2; const bw = Math.max(1, Math.min(28, (grupo * 0.7 - gap * (series.length - 1)) / series.length, (grupo * 0.8) / series.length));
  const y = (v) => top + alt - (Number(v || 0) / max) * alt;
  const fe = formatoEixo || formato;
  return (
    <div className="graf" ref={ref}>
      <Legenda series={series} />
      {total === 0 ? <div className="graf-vazio" style={{ height: altura }}>{vazio}</div> : w > 0 && (
        <svg width={w} height={altura} role="img" aria-label={series.map((s) => s.nome).join(' e ')} onMouseLeave={() => setHover(null)}>
          {[0, 0.5, 1].map((f) => (
            <g key={f}>
              <line x1={esq} x2={w - dir} y1={y(max * f)} y2={y(max * f)} stroke="#E6EAEF" strokeWidth="1" />
              <text x={esq - 8} y={y(max * f) + 4} textAnchor="end" className="graf-eixo">{fe(max * f)}</text>
            </g>
          ))}
          {dados.map((d, i) => {
            const x0 = esq + i * grupo + (grupo - (bw * series.length + gap * (series.length - 1))) / 2;
            return (
              <g key={d.rotulo} onMouseEnter={() => setHover(i)} onClick={() => setHover(i)}>
                <rect x={esq + i * grupo} y={top} width={grupo} height={alt} fill={hover === i ? '#F1F5FA' : 'transparent'} />
                {series.map((s, k) => {
                  const v = Number(d[s.chave] || 0); const h = Math.max(0, top + alt - y(v));
                  const x = x0 + k * (bw + gap); const r = Math.min(4, bw / 2, h);
                  return h > 0 ? <path key={s.chave} fill={s.cor || CORES_SERIE[k]}
                    d={`M${x},${top + alt} V${top + alt - h + r} Q${x},${top + alt - h} ${x + r},${top + alt - h} H${x + bw - r} Q${x + bw},${top + alt - h} ${x + bw},${top + alt - h + r} V${top + alt} Z`} /> : null;
                })}
                <text x={esq + i * grupo + grupo / 2} y={altura - 8} textAnchor="middle" className="graf-eixo">{d.rotulo}</text>
              </g>
            );
          })}
          <line x1={esq} x2={w - dir} y1={top + alt} y2={top + alt} stroke="#BFC7D1" strokeWidth="1" />
        </svg>
      )}
      {hover !== null && dados[hover] && (
        <div className="graf-tip" style={{ left: Math.min(Math.max(esq + hover * grupo + grupo / 2, 90), Math.max(90, w - 90)) }}>
          <b>{dados[hover].rotuloLongo || dados[hover].rotulo}</b>
          {series.map((s, k) => <div key={s.chave}><i style={{ background: s.cor || CORES_SERIE[k] }} />{s.nome}: <strong>{formato(dados[hover][s.chave])}</strong></div>)}
        </div>
      )}
    </div>
  );
}

// Uma barra horizontal dividida em partes. partes: [{ nome, valor, cor }]
export function BarraPartes({ partes, total, formato = (v) => v }) {
  const t = total || partes.reduce((s, p) => s + Number(p.valor || 0), 0);
  return (
    <div className="graf-partes">
      <div className="barra">
        {t > 0 && partes.filter((p) => p.valor > 0).map((p) => (
          <span key={p.nome} title={`${p.nome}: ${formato(p.valor)}`} style={{ width: (p.valor / t) * 100 + '%', background: p.cor }} />
        ))}
      </div>
      <div className="graf-legenda">
        {partes.map((p) => <span key={p.nome}><i style={{ background: p.cor }} />{p.nome} <strong>{formato(p.valor)}</strong></span>)}
      </div>
    </div>
  );
}
